// Application constants

export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: "/auth/login",
    REFRESH: "/auth/refresh",
    LOGOUT: "/auth/logout",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password",
    CHANGE_PASSWORD: "/auth/change-password",
    MFA_LOGIN: "/auth/mfa/login",
    MFA_SETUP_GET: "/auth/mfa/setup",
    MFA_SETUP_POST: "/auth/mfa/setup",
    MFA_DISABLE: "/auth/mfa",
  },

  // Dashboard — GET returns role-scoped payload (backend reads JWT); optional sections omitted per role
  DASHBOARD: {
    GET: "/dashboard",
    /** @deprecated Prefer GET /dashboard */
    GET_SUMMARY_CARDS: "/dashboard/summary-cards",
  },

  // Clients
  CLIENTS: {
    GET_ALL: "/clients",
    GET_ALL_LIST: "/clients/all",
    GET_BY_ID: (id) => `/clients/${id}`,
    GET_DASHBOARD: (id) => `/clients/${id}/dashboard`,
    CREATE: "/clients",
    UPDATE: (id) => `/clients/${id}`,
    DELETE: (id) => `/clients/${id}`,
    EXPORT: "/clients/export",

    GET_ATTACHMENTS: (clientId) => `/clients/${clientId}/attachments`,
    UPLOAD_ATTACHMENTS: (clientId) => `/clients/${clientId}/attachments`,
    GET_ATTACHMENT_URL: (clientId, attachmentId) =>
      `/clients/${clientId}/attachments/${attachmentId}/url`,
    DELETE_ATTACHMENT: (clientId, attachmentId) =>
      `/clients/${clientId}/attachments/${attachmentId}`,
    GET_SAAS_MODULES: "/clients/saas-modules",
    UPDATE_SAAS_MASTER_MODULE: (id) => `/clients/saas-modules/${id}`,
    DELETE_SAAS_MASTER_MODULE: (id) => `/clients/saas-modules/${id}`,
    GET_CLIENT_SAAS_MODULES: (clientId) => `/clients/${clientId}/saas-modules`,
    SET_SAAS_MODULES: (clientId) => `/clients/${clientId}/saas-modules`,
    HOURS_SUMMARY_EMAIL: (clientId) => `/clients/${clientId}/hours-summary/email`,
    HOURS_SUMMARY_EXPORT: (clientId) => `/clients/${clientId}/hours-summary/export`,
  },

  // Users
  USERS: {
    GET_ALL: "/users",
    GET_ALL_LIST: "/users/all",
    CREATE: "/users",
    GET_BY_ID: (id) => `/users/${id}`,
    UPDATE: (id) => `/users/${id}`,
    DELETE: (id) => `/users/${id}`,
    VERIFY_EMAIL: "/users/verify-email",
    SEND_VERIFICATION_EMAIL: (id) => `/users/${id}/send-verification-email`,
    /** Current user profile — GET /users/me */
    ME: "/users/me",
    ME_UPDATE: "/users/me",
    ME_PASSWORD: "/users/me/password",
    GET_ROLES: "/users/roles",
  },

  /** Typed tags (CLIENT_TAG, VENDOR_TAG, ACCOUNTING_TAG) — POST body: { listType, name } */
  TAGS: {
    CREATE: "/tags",
  },

  /** Source → accounting tag links — GET query: mappingType, activeOnly */
  TAG_MAPPINGS: {
    GET_ALL: "/tag-mappings",
    GET_BY_ID: (id) => `/tag-mappings/${id}`,
    CREATE: "/tag-mappings",
    UPDATE: (id) => `/tag-mappings/${id}`,
    DELETE: (id) => `/tag-mappings/${id}`,
  },

  // Projects
  PROJECTS: {
    GET_ALL: "/projects",
    GET_ALL_LIST: "/projects/all",
    EXPORT: "/projects/export",
    CREATE: "/projects",
    /** GET single project — same resource as UPDATE */
    GET_BY_ID: (id) => `/projects/${id}`,
    UPDATE: (id) => `/projects/${id}`,
    DELETE: (id) => `/projects/${id}`,
    /** Project tag assignments — GET returns system_list rows; PUT replaces assignments */
    GET_TAGS: (id) => `/projects/${id}/tags`,
    PUT_TAGS: (id) => `/projects/${id}/tags`,
    /** Documents — uploaded via POST/PUT /projects (multipart `files`); managed separately here */
    GET_ATTACHMENTS: (projectId) => `/projects/${projectId}/attachments`,
    GET_ATTACHMENT_URL: (projectId, attachmentId) =>
      `/projects/${projectId}/attachments/${attachmentId}/url`,
    DELETE_ATTACHMENT: (projectId, attachmentId) =>
      `/projects/${projectId}/attachments/${attachmentId}`,
    GET_DASHBOARD: (id) => `/projects/${id}/dashboard`,
    GET_STATUSES: (id) => `/projects/${id}/statuses`,
    CREATE_STATUS: (id) => `/projects/${id}/statuses`,
    UPDATE_STATUS: (projectId, statusId) => `/projects/${projectId}/statuses/${statusId}`,
    /** GET `?format=csv` | `?format=xlsx` — export work items for the project */
    EXPORT_WORK_ITEMS: (projectId) => `/projects/${projectId}/work-items/export`,
    CREATE_WORK_ITEM: (projectId) => `/projects/${projectId}/work-items`,
    /** Epics only — Parent epic dropdown (create/edit work item) */
    GET_WORK_ITEM_EPICS: (projectId) => `/projects/${projectId}/work-items/epics`,
    GET_KANBAN_INITIAL: (projectId) => `/projects/${projectId}/work-items/kanban/initial`,
    GET_WORK_ITEM: (projectId, workItemId) => `/projects/${projectId}/work-items/${workItemId}`,
    UPDATE_WORK_ITEM: (projectId, workItemId) => `/projects/${projectId}/work-items/${workItemId}`,
    DELETE_WORK_ITEM: (projectId, workItemId) => `/projects/${projectId}/work-items/${workItemId}`,
    GET_WORK_ITEM_COMMENTS: (projectId, workItemId) =>
      `/projects/${projectId}/work-items/${workItemId}/comments`,
    GET_WORK_ITEM_HISTORY: (projectId, workItemId) =>
      `/projects/${projectId}/work-items/${workItemId}/history`,
    GET_WORK_ITEM_TIME_LOGS: (projectId, workItemId) =>
      `/projects/${projectId}/work-items/${workItemId}/time-logs`,
    ADD_WORK_ITEM_COMMENT: (projectId, workItemId) =>
      `/projects/${projectId}/work-items/${workItemId}/comments`,
    UPDATE_WORK_ITEM_COMMENT: (projectId, workItemId, commentId) =>
      `/projects/${projectId}/work-items/${workItemId}/comments/${commentId}`,
    DELETE_WORK_ITEM_COMMENT: (projectId, workItemId, commentId) =>
      `/projects/${projectId}/work-items/${workItemId}/comments/${commentId}`,
    GET_WORK_ITEM_COMMENT_ATTACHMENT_URL: (projectId, workItemId, commentId, attachmentId) =>
      `/projects/${projectId}/work-items/${workItemId}/comments/${commentId}/attachments/${attachmentId}/url`,
    DELETE_WORK_ITEM_COMMENT_ATTACHMENT: (projectId, workItemId, commentId, attachmentId) =>
      `/projects/${projectId}/work-items/${workItemId}/comments/${commentId}/attachments/${attachmentId}`,
    ADD_WORK_ITEM_TIME_LOG: (projectId, workItemId) =>
      `/projects/${projectId}/work-items/${workItemId}/time-logs`,
    UPLOAD_WORK_ITEM_ATTACHMENTS: (projectId, workItemId) =>
      `/projects/${projectId}/work-items/${workItemId}/attachments`,
    LIST_WORK_ITEM_ATTACHMENTS: (projectId, workItemId) =>
      `/projects/${projectId}/work-items/${workItemId}/attachments`,
    GET_WORK_ITEM_ATTACHMENT_URL: (projectId, workItemId, attachmentId) =>
      `/projects/${projectId}/work-items/${workItemId}/attachments/${attachmentId}/url`,
    DELETE_WORK_ITEM_ATTACHMENT: (projectId, workItemId, attachmentId) =>
      `/projects/${projectId}/work-items/${workItemId}/attachments/${attachmentId}`,
    /** Project settings (General + Notification) — GET | PATCH | PUT */
    GET_SETTINGS: (projectId) => `/projects/${projectId}/settings`,
    PATCH_SETTINGS: (projectId) => `/projects/${projectId}/settings`,
  },

  // Teams
  TEAMS: {
    GET_ALL: "/teams",
    GET_ALL_LIST: "/teams/all",
    CREATE: "/teams",
    UPDATE: (id) => `/teams/${id}`,
    GET_BY_ID: (id) => `/teams/${id}`,
    DELETE: (id) => `/teams/${id}`,
    ADD_MEMBER: (teamId) => `/teams/${teamId}/members`,
    /** PATCH / DELETE — team membership for a user */
    TEAM_MEMBER: (teamId, userId) => `/teams/${teamId}/members/${userId}`,
    GET_USERS: (teamId) => `/teams/${teamId}/users`,
  },

  // Issues
  ISSUES: {
    GET_ALL: "/issues",
    GET_BY_ID: (id) => `/issues/${id}`,
  },

  // Work Items
  // GET /work-items – paged all work items (Issues list tab)
  // GET /work-items/all – all work items for global Kanban board
  // GET /work-items/:projectId?limit=50 – work items by project (projectId in path)
  WORK_ITEMS: {
    GET_ALL: "/work-items",
    GET_ALL_GLOBAL_KANBAN: "/work-items/all",
    GET_KANBAN_INITIAL: "/work-items/kanban/initial",
    GET_MY: "/work-items/my",
    /** My work items — id + title only; same filters as GET_MY; pagination ignored */
    GET_MY_ALL: "/work-items/my/all",
    GET_BY_PROJECT: (projectId) => `/work-items/${projectId}`,
    // Saved searches — GET/POST /saved-search, PUT/DELETE /saved-search/:id
    SAVED_SEARCH: "/work-items/saved-search",
    SAVED_SEARCH_BY_ID: (id) => `/work-items/saved-search/${id}`,
  },

  // Backlog
  BACKLOG: {
    GET_ALL: "/backlog",
  },

  // Time Entries / Timesheet
  TIME_ENTRIES: {
    GET_ALL: "/time-entries",
    /** My time logs for timesheet tab */
    GET_MY_TIME_LOGS: "/time-logs/my",
    /** All accessible time logs (list with filters + pagination) */
    GET_ALL_TIME_LOGS: "/time-logs",
    UPDATE_MY_TIME_LOG: (id) => `/time-logs/my/${id}`,
    DELETE_MY_TIME_LOG: (id) => `/time-logs/my/${id}`,
    GET_TEAM_SUMMARY: "/time-logs/team-summary",
    GET_PROJECT_SUMMARY: "/time-logs/project-summary",
    /** GET — Developer Time Entry table on the Team Time Entry tab (grouped by developer + work item + date) */
    GET_DEVELOPER_SUMMARY: "/time-logs/developer-summary",
    /** GET `?format=csv|xlsx` — current user’s timesheet export */
    EXPORT_MY: "/time-logs/my/export",
    /** GET `?format=csv|xlsx` — all accessible time logs (All Time Entries tab) */
    EXPORT_ALL_TIME_LOGS: "/time-logs/export",
    /** GET `?format=csv|xlsx` — team summary export (requires TIME_ENTRY_TEAM_SUMMARY_VIEW) */
    EXPORT_TEAM_SUMMARY: "/time-logs/team-summary/export",
    /** GET `?format=csv|xlsx` — project summary export (requires TIME_ENTRY_PROJECT_SUMMARY_VIEW) */
    EXPORT_PROJECT_SUMMARY: "/time-logs/project-summary/export",
    /** GET `?format=csv|xlsx` — developer summary export (requires TIME_ENTRY_TEAM_SUMMARY_VIEW) */
    EXPORT_DEVELOPER_SUMMARY: "/time-logs/developer-summary/export",
  },

  // Billing - Tag Mapping-Client QB Mapping-User Rate Mapping
  TAB_MAPPING: {
    GET_ALL: "/tab-mapping",
    UPDATE: (id) => `/tab-mapping/${id}`,
    DELETE: (id) => `/tab-mapping/${id}`,
    GET_CLIENT_QB_MAPPING: "/client-qb-mapping",
    GET_USER_RATE_MAPPING: "/user-rate-mapping",
  },

  // Vendor Tag Mapping (Settings)
  VENDOR_TAG_MAPPING: {
    GET_ALL: "/vendor-tab-mapping",
    CREATE: "/vendor-tab-mapping",
    UPDATE: (id) => `/vendor-tab-mapping/${id}`,
    DELETE: (id) => `/vendor-tab-mapping/${id}`,
  },

  // Billing - Exports & QuickBooks
  BILLING: {
    /** @deprecated Preview data — use QB_REPORTS */
    EXPORTS_PREVIEW: "/billing/exports-preview",
    /** GET `?type=saas|qb` — history; POST JSON `{ format, reportType, filters }` — create export */
    EXPORTS: "/billing/exports",
    /** GET — download export file (Bearer); binary response */
    EXPORT_DOWNLOAD: (exportId) => `/billing/exports/${exportId}/download`,
    SAAS_MODULE: "/billing/saas-module",
    /** GET — Export Preview (filters: clientId, projectId, userId, teamId, startDate, endDate) */
    QB_REPORTS: "/qb/reports",
    /** GET — file export (same query params as {@link QB_REPORTS}) */
    QB_REPORTS_EXPORT: "/qb/reports/export",
  },

  //More Spaces
  // existing...
  MORES_SPACES: "/morespaces",

  // Reports - Month Locking & Audit
  REPORTS: {
    /** Create month lock — body: { month: "YYYY-MM", useAutoLock: boolean, clientId?: uuid } */
    MONTH_LOCKS: "/month-locks",
    /** Unlock month — body: { reason: string } */
    MONTH_LOCK_UNLOCK: (lockId) => `/month-locks/${lockId}/unlock`,
    /** Month lock audit trail — query: action, user_id, date_from, date_to, page, limit */
    AUDIT_LOGS: "/audit-logs",
    /** Audit log CSV/XLSX export — query: action, user_id, date_from, date_to, format */
    AUDIT_LOGS_EXPORT: "/audit-logs/export",
  },

  // System lists (e.g. job titles, listType=JOB_TITLE; tag CRUD under Settings → System lists)
  SYSTEM_LISTS: {
    GET_ALL: "/system-lists",
    BY_ID: (id) => `/system-lists/${id}`,
  },

  // Roles
  ROLES: {
    GET_ALL: "/roles",
    CREATE: "/roles",
    UPDATE: (id) => `/roles/${id}`,
    DELETE: (id) => `/roles/${id}`,
  },

  // Permissions
  PERMISSIONS: {
    GET_ALL: "/permissions",
    GET_BY_ROLE: (roleId) => `/roles/${roleId}/permissions`,
    UPDATE_ROLE_PERMISSIONS: (roleId) => `/roles/${roleId}/permissions`,
  },
};

// GET { { baseUrl } }/system-lists?listType=WORK_ITEM_TYPE
// Authorization: Bearer { { adminToken } }

// GET { { baseUrl } }/system-lists?listType=PRIORITY
// Authorization: Bearer { { adminToken } }
